// [Codex 2026-07-03] Rich document blocks for generated memo/File Room output.
import type { CSSProperties } from "react";
import { FintheonNumericText } from "../../numbers/FintheonNumericText";
import type { CustomRendererProps } from "streamdown";
import {
  isBriefingBlockKind,
  labelForBriefingKind,
  renderBriefingBlock,
  type BriefingBlockKind,
} from "./DocumentRichBriefingBlock";
import { parseSlotBody } from "./parseSlotBody";
import { SlotError, SlotReveal, SlotShell, SlotSkeleton } from "./SlotShell";
import "./document-rich-block.css";

type DocumentRichKind =
  | "table"
  | "chartSpec"
  | "graphSpec"
  | "headlineMedia"
  | "citationLink"
  | BriefingBlockKind;

interface DocumentRichBlock {
  kind: DocumentRichKind;
  payload: Record<string, unknown>;
}

type DataPoint = { label: string; value: number; note?: string };
type GraphNode = { id: string; label: string };
type GraphEdge = { from: string; to: string };

export function DocumentRichBlockSlot({
  code,
  isIncomplete,
}: CustomRendererProps) {
  const parsed = parseSlotBody<DocumentRichBlock>(code, isIncomplete);
  if (parsed.status === "pending")
    return <SlotSkeleton label="document block" lines={4} />;
  if (parsed.status === "error")
    return <SlotError label="document block" reason={parsed.reason} />;

  const block = normalizeBlock(parsed.data);
  if (!block)
    return <SlotError label="document block" reason="Schema mismatch" />;

  return (
    <SlotReveal>
      <SlotShell label={labelForKind(block.kind)}>
        <article className="document-rich" data-kind={block.kind}>
          {renderBlock(block)}
        </article>
      </SlotShell>
    </SlotReveal>
  );
}

function renderBlock(block: DocumentRichBlock) {
  if (isBriefingBlockKind(block.kind))
    return renderBriefingBlock({ kind: block.kind, payload: block.payload });
  if (block.kind === "table") return <RichTable payload={block.payload} />;
  if (block.kind === "chartSpec") return <RichChart payload={block.payload} />;
  if (block.kind === "graphSpec") return <RichGraph payload={block.payload} />;
  if (block.kind === "headlineMedia")
    return <RichHeadline payload={block.payload} />;
  return <RichCitation payload={block.payload} />;
}

function RichTable({ payload }: { payload: Record<string, unknown> }) {
  const columns = readStringList(payload.columns, [
    "Element",
    "Read",
    "Action",
  ]);
  const rows = normalizeRows(payload.rows, columns.length);
  return (
    <div className="document-rich__table-wrap">
      <table className="document-rich__table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column}>{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={`${row.join("|")}-${index}`}>
              {row.map((cell, cellIndex) => (
                <td key={`${cell}-${cellIndex}`}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RichChart({ payload }: { payload: Record<string, unknown> }) {
  const title = readString(payload.title, "Generated chart");
  const type = readString(payload.type, "bar");
  const note = readString(payload.note, "");
  const points = normalizePoints(payload.data);
  const max = Math.max(...points.map((point) => Math.abs(point.value)), 1);
  return (
    <section className="document-rich__chart">
      <Header title={title} meta={type} />
      <div className="document-rich__bars" role="list">
        {points.map((point) => (
          <div
            key={point.label}
            className="document-rich__bar-row"
            style={barStyle(point.value, max)}
            title={point.note ?? `${point.label}: ${formatNumber(point.value)}`}
          >
            <span>{point.label}</span>
            <FintheonNumericText text={formatNumber(point.value)} />
          </div>
        ))}
      </div>
      {note ? <p className="document-rich__note">{note}</p> : null}
    </section>
  );
}

function RichGraph({ payload }: { payload: Record<string, unknown> }) {
  const title = readString(payload.title, "Generated graph");
  const nodes = normalizeNodes(payload.nodes);
  const edges = normalizeEdges(payload.edges);
  const labels = new Map(nodes.map((node) => [node.id, node.label]));
  return (
    <section className="document-rich__graph">
      <Header title={title} meta={`${nodes.length} nodes`} />
      <div className="document-rich__nodes" role="list">
        {nodes.map((node) => (
          <span key={node.id} className="document-rich__node" title={node.id}>
            {node.label}
          </span>
        ))}
      </div>
      <div className="document-rich__edges">
        {edges.map((edge, index) => (
          <div key={`${edge.from}-${edge.to}-${index}`}>
            <span>{labels.get(edge.from) ?? edge.from}</span>
            <span aria-hidden="true">{"->"}</span>
            <span>{labels.get(edge.to) ?? edge.to}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function RichHeadline({ payload }: { payload: Record<string, unknown> }) {
  const headline = readString(payload.headline, "Source-backed headline");
  const summary = readString(payload.summary, "");
  const mediaType = readString(payload.mediaType, "headline");
  const source = readString(payload.source, "");
  return (
    <section className="document-rich__headline">
      <Header title={headline} meta={mediaType} />
      {summary ? <p>{summary}</p> : null}
      {source ? <p className="document-rich__source">{source}</p> : null}
    </section>
  );
}

function RichCitation({ payload }: { payload: Record<string, unknown> }) {
  const label = readString(payload.label, "Source note");
  const note = readString(payload.note, "Attach verified source.");
  const url = readString(payload.url, "");
  return (
    <section className="document-rich__citation">
      <Header title={label} meta="citation" />
      <p>{note}</p>
      {url ? (
        <a href={url} target="_blank" rel="noreferrer">
          {url}
        </a>
      ) : null}
    </section>
  );
}

function Header({ title, meta }: { title: string; meta: string }) {
  return (
    <header className="document-rich__header">
      <h3>{title}</h3>
      <span>{meta}</span>
    </header>
  );
}

function normalizeBlock(value: unknown): DocumentRichBlock | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const kind = record.kind;
  if (!isDocumentRichKind(kind)) return null;
  return {
    kind,
    payload:
      record.payload && typeof record.payload === "object"
        ? (record.payload as Record<string, unknown>)
        : {},
  };
}

function isDocumentRichKind(value: unknown): value is DocumentRichKind {
  return (
    isBriefingBlockKind(value) ||
    value === "table" ||
    value === "chartSpec" ||
    value === "graphSpec" ||
    value === "headlineMedia" ||
    value === "citationLink"
  );
}

function normalizeRows(value: unknown, width: number): string[][] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((row): row is unknown[] => Array.isArray(row))
    .map((row) =>
      Array.from({ length: width }, (_item, index) => String(row[index] ?? "")),
    );
}

function normalizePoints(value: unknown): DataPoint[] {
  if (!Array.isArray(value)) return [{ label: "Value", value: 1 }];
  const points = value
    .filter((point): point is Record<string, unknown> => isRecord(point))
    .map((point) => ({
      label: readString(point.label, "Point"),
      value: readNumber(point.value, 0),
      note: readString(point.note, ""),
    }))
    .filter((point) => Number.isFinite(point.value));
  return points.length > 0 ? points : [{ label: "Value", value: 1 }];
}

function normalizeNodes(value: unknown): GraphNode[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRecord).map((node, index) => ({
    id: readString(node.id, `node-${index + 1}`),
    label: readString(node.label, readString(node.id, `Node ${index + 1}`)),
  }));
}

function normalizeEdges(value: unknown): GraphEdge[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRecord).map((edge) => ({
    from: readString(edge.from, ""),
    to: readString(edge.to, ""),
  }));
}

function readString(value: unknown, fallback: string): string {
  return typeof value === "string" && value.trim() ? value.trim() : fallback;
}

function readStringList(value: unknown, fallback: string[]): string[] {
  if (!Array.isArray(value)) return fallback;
  const list = value
    .map(String)
    .map((item) => item.trim())
    .filter(Boolean);
  return list.length > 0 ? list : fallback;
}

function readNumber(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object";
}

function labelForKind(kind: DocumentRichKind): string {
  if (isBriefingBlockKind(kind)) return labelForBriefingKind(kind);
  if (kind === "chartSpec") return "document chart";
  if (kind === "graphSpec") return "document graph";
  if (kind === "headlineMedia") return "document media";
  if (kind === "citationLink") return "document source";
  return "document table";
}

function barStyle(value: number, max: number): CSSProperties {
  return {
    "--document-rich-bar": `${Math.max(4, Math.abs(value / max) * 100)}%`,
  } as CSSProperties;
}

function formatNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}
